import React from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';

export interface AvatarProps {
  src?: string;
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  online?: boolean;
  className?: string;
}

const sizeMap = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-base',
  xl: 'h-20 w-20 text-xl',
};

const onlineIndicatorSize = {
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
  xl: 'h-3.5 w-3.5',
};

const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = 'md',
  online = false,
  className,
}) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      {src ? (
        <div
          className={cn(
            'relative overflow-hidden rounded-full ring-2 ring-neon-cyan/30',
            sizeMap[size]
          )}
        >
          <Image
            src={src}
            alt={name}
            fill
            sizes="(max-width: 768px) 80px, 56px"
            className="object-cover"
          />
        </div>
      ) : (
        <div
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-panel font-semibold text-foreground ring-2 ring-neon-cyan/30',
            sizeMap[size]
          )}
        >
          {initials}
        </div>
      )}
      {online && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-2 border-obsidian bg-neon-green shadow-glow-cyan',
            onlineIndicatorSize[size]
          )}
        />
      )}
    </div>
  );
};

export default Avatar;
